import { useCallback, useEffect, useMemo, useState } from 'react'

import { browser } from '@web/constants/browserapi'
import {
  getRailgunShieldTxLog,
  parseRailgunShieldTxLog,
  RAILGUN_SHIELD_TX_LOG_KEY,
  type RailgunShieldTxRecord
} from '@web/utils/railgunShieldTxStorage'

type UseRailgunShieldTxLogOptions = {
  /** When set, `records` only includes entries for this account (case-insensitive). */
  accountAddr?: string | null
}

export function useRailgunShieldTxLog(options?: UseRailgunShieldTxLogOptions) {
  const accountAddr = options?.accountAddr
  const [allRecords, setAllRecords] = useState<RailgunShieldTxRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      const list = await getRailgunShieldTxLog()
      setAllRecords(list)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load shield tx log'
      setError(message)
      setAllRecords([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    setIsLoading(true)
    void load()
  }, [load])

  useEffect(() => {
    if (!browser?.storage?.onChanged) return

    const handleStorageChange = (
      changes: { [key: string]: chrome.storage.StorageChange },
      areaName: string
    ) => {
      if (areaName !== 'local' || !changes[RAILGUN_SHIELD_TX_LOG_KEY]) return
      const { newValue } = changes[RAILGUN_SHIELD_TX_LOG_KEY]!
      setAllRecords(parseRailgunShieldTxLog(newValue))
      setIsLoading(false)
    }

    browser.storage.onChanged.addListener(handleStorageChange)
    return () => {
      browser.storage.onChanged.removeListener(handleStorageChange)
    }
  }, [])

  const records = useMemo(() => {
    if (!accountAddr) return allRecords
    const lower = accountAddr.toLowerCase()
    return allRecords.filter((r) => r.accountAddr.toLowerCase() === lower)
  }, [allRecords, accountAddr])

  const refresh = useCallback(async () => {
    setIsLoading(true)
    await load()
  }, [load])

  return { records, isLoading, error, refresh }
}
